/**
 * 処理ステップタイムライン
 */

import { Timeline, Text } from "@mantine/core";
import { Check, X, Loader, AlertCircle, Minus } from "lucide-react";
import { EmailLog, ProcessStep } from "../../services/logService";
import { MASTER_STEPS, MasterStepDefinition, StepStatus } from "../../constants/stepDefinitions";
import { StepContentRenderer } from "./StepContentRenderer";
import { formatTime } from "../../utils/formatters";

interface StepTimelineProps {
  steps: ProcessStep[];
  log: EmailLog;
}

interface StepDisplayResult {
  status: StepStatus;
  log: ProcessStep | null;
  icon: React.ReactNode;
  color: string;
}

/**
 * 各ステップの状態と表示設定を取得
 */
function getStepDisplay(masterStep: MasterStepDefinition, logs: ProcessStep[]): StepDisplayResult {
  // このマスターステップに該当する最新のログを探す
  const relevantLogs = logs.filter((l) => masterStep.types.includes(l.type));
  const log = relevantLogs.length > 0 ? relevantLogs[relevantLogs.length - 1] : null;

  if (log) {
    if (log.status === "error")
      return { status: "error", log, icon: <X size={12} />, color: "red" };
    if (log.status === "warning")
      return { status: "warning", log, icon: <AlertCircle size={12} />, color: "orange" };
    if (log.status === "processing")
      return {
        status: "processing",
        log,
        icon: <Loader size={12} className="animate-spin" />,
        color: "blue",
      };
    if (log.status === "success")
      return { status: "completed", log, icon: <Check size={12} />, color: "green" };
    if ((log.status as string) === "info" || (log.status as string) === "skipped")
      return { status: "skipped", log, icon: <AlertCircle size={12} />, color: "orange" };
    return { status: "completed", log, icon: <Check size={12} />, color: "teal" };
  }

  // ログがない場合
  // 既に先のステップに到達しているか
  const currentStepIdx = MASTER_STEPS.findIndex((s) => s.id === masterStep.id);
  const reachedFarther = logs.some((l) => {
    const logStepIdx = MASTER_STEPS.findIndex((ms) => ms.types.includes(l.type));
    return logStepIdx > currentStepIdx;
  });

  if (reachedFarther) {
    return { status: "skipped", log: null, icon: <Minus size={12} />, color: "gray" };
  }

  return { status: "pending", log: null, icon: null, color: "gray" };
}

export function StepTimeline({ steps, log }: StepTimelineProps) {
  // 最後に到達したステップのインデックスを探す
  let activeIndex = -1;
  MASTER_STEPS.forEach((ms, idx) => {
    if (steps.some((l) => ms.types.includes(l.type))) {
      activeIndex = idx;
    }
  });

  return (
    <Timeline active={activeIndex} bulletSize={20} lineWidth={2}>
      {MASTER_STEPS.map((masterStep) => {
        const display = getStepDisplay(masterStep, steps);
        const isPending = display.status === "pending";

        return (
          <Timeline.Item
            key={masterStep.id}
            bullet={display.icon}
            color={display.color}
            title={
              <Text size="sm" fw={isPending ? 500 : 700} c={isPending ? "dimmed" : "inherit"}>
                {masterStep.name}
                {display.status === "skipped" && !display.log && "（スキップ）"}
              </Text>
            }
            opacity={isPending ? 0.5 : 1}
          >
            <StepContentRenderer step={masterStep} log={display.log} logData={log} />
            {display.log && (
              <Text size="10px" c="dimmed" mt={2}>
                {formatTime(display.log.timestamp)}
              </Text>
            )}
          </Timeline.Item>
        );
      })}
    </Timeline>
  );
}
